import React, { useState } from 'react';
import { Image, KeyboardAvoidingView, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { showError } from '../toast';
import { MapBackground } from '../map-background';
import { useWide } from '../layout';
import { Button, Card, COLORS, Constrain, Input, Label, Muted, Title } from '../ui';
import { getLocale, setLocaleChoice, t } from '../i18n';

export default function LoginScreen({ navigation }: any) {
  const wide = useWide();
  const { signIn } = useAuth();
  const insets = useSafeAreaInsets();
  const [mode, setMode] = useState<'password' | 'sms'>('password');
  const [phone, setPhone] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const requestOtp = async () => {
    setLoading(true);
    try {
      const res = await api<{ phone: string; alreadySent?: boolean }>('/auth/otp/request', { body: { phone } });
      navigation.navigate('Otp', { phone: res.phone, alreadySent: res.alreadySent });
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const passwordLogin = async () => {
    setLoading(true);
    try {
      const res = await api('/auth/login', { body: { identifier, password } });
      await signIn(res);
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <MapBackground />
      <ScrollView
        contentContainerStyle={{ padding: 16, paddingBottom: 48 }}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
      >
      <Constrain>
      {/* Входът е тясна централна карта — формуляр на цял широк екран
          изглежда разтегнат. На телефон нищо не се променя. */}
      <View style={{ marginTop: insets.top + (wide ? 40 : 90), maxWidth: wide ? 440 : undefined, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <Image source={require('../../assets/logo-gradient.png')} style={{ width: 44, height: 44 }} resizeMode="contain" />
          <Title style={{ marginBottom: 0 }}>Livupp</Title>
          <View style={{ flex: 1 }} />
          {/* Дискретен превключвател на езика — BG | EN */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 4 }}>
            {(['bg', 'en'] as const).map((code, i) => (
              <React.Fragment key={code}>
                {i > 0 && <Text style={{ color: COLORS.muted, fontSize: 13 }}>|</Text>}
                <Pressable onPress={() => setLocaleChoice(code)} hitSlop={8}>
                  <Text
                    style={{
                      fontSize: 13,
                      color: getLocale() === code ? COLORS.primary : COLORS.muted,
                      fontWeight: getLocale() === code ? '700' : '500',
                    }}
                  >
                    {code.toUpperCase()}
                  </Text>
                </Pressable>
              </React.Fragment>
            ))}
          </View>
        </View>
        <Card>
          <View style={{ flexDirection: 'row', backgroundColor: COLORS.bg, borderRadius: 10, padding: 4, marginBottom: 12 }}>
            {(
              [
                ['password', t('Вход с парола')],
                ['sms', t('Първи вход (наемател)')],
              ] as const
            ).map(([key, label]) => (
              <Pressable
                key={key}
                onPress={() => setMode(key)}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 8,
                  alignItems: 'center',
                  backgroundColor: mode === key ? COLORS.card : 'transparent',
                }}
              >
                <Text style={{ fontWeight: mode === key ? '700' : '400', color: COLORS.text }}>{label}</Text>
              </Pressable>
            ))}
          </View>

          {mode === 'password' ? (
            <>
              <Label>{t('Телефон или имейл')}</Label>
              <Input
                placeholder={t('+359 88 812 3456 или ime@poshta.bg')}
                autoCapitalize="none"
                keyboardType="email-address"
                value={identifier}
                onChangeText={setIdentifier}
              />
              <Label>{t('Парола')}</Label>
              <Input placeholder="••••••••" secureTextEntry value={password} onChangeText={setPassword} />
              <Button title={t('Вход')} onPress={passwordLogin} disabled={!identifier || !password} loading={loading} style={{ alignSelf: 'stretch' }} />
              <Button title={t('Забравена парола?')} variant="ghost" onPress={() => navigation.navigate('ForgotPassword')} />
            </>
          ) : (
            <>
              <Muted>
                {t('Само за поканен наемател/собственик — при първия вход получавате SMS код и си задавате парола. След това влизате с нея. Криейтърите се регистрират от линка в поканата си.')}
              </Muted>
              <Label>{t('Телефон (на който сте поканени)')}</Label>
              <Input
                placeholder="+359 88 812 3456"
                keyboardType="phone-pad"
                value={phone}
                onChangeText={setPhone}
              />
              <Button title={t('Изпрати код')} onPress={requestOtp} disabled={phone.length < 6} loading={loading} style={{ alignSelf: 'stretch' }} />
            </>
          )}
        </Card>

        <Card>
          <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text }}>{t('Нямате акаунт?')}</Text>
          <Muted>{t('Наемодател, пропърти мениджър или майстор — регистрирайте се с телефон и парола.')}</Muted>
          <Button title={t('Регистрация')} variant="secondary" onPress={() => navigation.navigate('Register', {})} style={{ alignSelf: 'stretch' }} />
        </Card>

        <Card>
          <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text }}>{t('Провери наемател 🔍')}</Text>
          <Muted>{t('Въведи телефона на наемател (или наемодател, майстор) — виж дали има отзиви, преди да дадеш ключовете.')}</Muted>
          <Button title={t('Провери наемател')} variant="secondary" onPress={() => navigation.navigate('PublicReputation')} style={{ alignSelf: 'stretch' }} />
        </Card>
      </View>
      </Constrain>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
